import { ArrowForwardRounded, HomeRounded } from "@mui/icons-material";
import { Box, Button, Chip, Paper, Stack, Typography } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../app/hooks";
import { UserRoles, type UserRole } from "../../constants/roles";

const ADUT_LOGO = "/images/adut-logo.svg";

const getHomePath = (isAuthenticated: boolean, role?: UserRole | string) => {
  if (!isAuthenticated) {
    return "/";
  }

  if (role === UserRoles.SUPER_ADMIN) {
    return "/tenants";
  }

  if (role === UserRoles.TRUCK_DRIVER) {
    return "/truck-driver";
  }

  return "/dashboard";
};

const NotFoundPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const homePath = getHomePath(isAuthenticated, user?.role);

  return (
    <Box
      sx={{
        alignItems: "center",
        background: (theme) =>
          theme.palette.mode === "light"
            ? "radial-gradient(circle at 15% 15%, rgba(175, 32, 36, 0.12), transparent 32%), radial-gradient(circle at 85% 20%, rgba(0, 100, 148, 0.14), transparent 34%), linear-gradient(135deg, #f8fbfd 0%, #ffffff 48%, #f5f7fa 100%)"
            : "radial-gradient(circle at 18% 18%, rgba(175, 32, 36, 0.2), transparent 34%), radial-gradient(circle at 82% 22%, rgba(75, 159, 202, 0.2), transparent 36%), linear-gradient(135deg, #0f1419 0%, #121212 50%, #161b22 100%)",
        display: "flex",
        justifyContent: "center",
        minHeight: "100vh",
        overflow: "hidden",
        p: { xs: 2, sm: 4 },
        position: "relative",
        "&::before": {
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "50%",
          content: '""',
          height: 420,
          opacity: 0.45,
          position: "absolute",
          right: { xs: -260, md: -120 },
          top: { xs: -220, md: -160 },
          width: 420,
        },
        "&::after": {
          bgcolor: "primary.main",
          borderRadius: "50%",
          bottom: { xs: -140, md: -90 },
          boxShadow: (theme) =>
            theme.palette.mode === "light"
              ? "0 0 90px rgba(0, 100, 148, 0.22)"
              : "0 0 90px rgba(75, 159, 202, 0.28)",
          content: '""',
          height: 240,
          left: { xs: -160, md: -80 },
          opacity: 0.16,
          position: "absolute",
          width: 240,
        },
      }}
    >
      <Paper
        elevation={8}
        sx={{
          backdropFilter: "blur(18px)",
          background: (theme) =>
            theme.palette.mode === "light"
              ? "rgba(255, 255, 255, 0.9)"
              : "rgba(30, 30, 30, 0.86)",
          border: "1px solid",
          borderColor: (theme) =>
            theme.palette.mode === "light"
              ? "rgba(0, 100, 148, 0.14)"
              : "rgba(255, 255, 255, 0.12)",
          borderRadius: { xs: 4, sm: 6 },
          boxShadow: (theme) =>
            theme.palette.mode === "light"
              ? "0 28px 80px rgba(17, 24, 39, 0.12)"
              : "0 28px 80px rgba(0, 0, 0, 0.36)",
          maxWidth: 760,
          overflow: "hidden",
          p: { xs: 3, sm: 5, md: 6 },
          position: "relative",
          textAlign: "center",
          width: "100%",
          zIndex: 1,
          "&::before": {
            background:
              "linear-gradient(90deg, #af2024 0%, #006494 52%, #4B9FCA 100%)",
            content: '""',
            height: 5,
            left: 0,
            position: "absolute",
            right: 0,
            top: 0,
          },
        }}
      >
        <Stack alignItems="center" spacing={{ xs: 3, sm: 4 }}>
          <Box
            component="img"
            src={ADUT_LOGO}
            alt="ADUT Global"
            sx={{
              height: { xs: 56, sm: 70 },
              objectFit: "contain",
              width: { xs: 180, sm: 230 },
            }}
          />

          <Stack alignItems="center" spacing={2}>
            <Chip
              icon={<HomeRounded />}
              label="ADUT Fleet Management"
              sx={{
                borderColor: "primary.main",
                color: "primary.main",
                fontWeight: 700,
                px: 1,
              }}
              variant="outlined"
            />

            <Box sx={{ position: "relative" }}>
              <Typography
                aria-hidden="true"
                sx={{
                  background:
                    "linear-gradient(135deg, #af2024 0%, #006494 55%, #4B9FCA 100%)",
                  backgroundClip: "text",
                  fontSize: { xs: 96, sm: 132, md: 156 },
                  fontWeight: 900,
                  letterSpacing: { xs: -4, sm: -7 },
                  lineHeight: 0.86,
                  opacity: 0.16,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                404
              </Typography>
              <Typography
                component="h1"
                fontWeight={800}
                sx={{
                  inset: 0,
                  letterSpacing: -0.6,
                  position: "absolute",
                  top: "50%",
                  transform: "translateY(-44%)",
                }}
                variant="h3"
              >
                Page not found
              </Typography>
            </Box>

            <Typography
              color="text.secondary"
              sx={{
                fontSize: { xs: "0.95rem", sm: "1rem" },
                lineHeight: 1.7,
                maxWidth: 520,
              }}
              variant="body1"
            >
              This route is not available in ADUT. Head back home to keep
              managing your fleet, vehicles, trailers, and operations.
            </Typography>
          </Stack>

          <Button
            endIcon={<ArrowForwardRounded />}
            onClick={() => navigate(homePath, { replace: true })}
            size="large"
            startIcon={<HomeRounded />}
            sx={{
              borderRadius: 999,
              boxShadow: "0 14px 32px rgba(0, 100, 148, 0.26)",
              fontWeight: 700,
              height: 48,
              px: 4,
              textTransform: "none",
            }}
            variant="contained"
          >
            Go to home page
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default NotFoundPage;
